import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {DtoImportObjectContext} from 'features/core/shared/dto/dto.import.object.context';
import {DtoImportObjectContextEntry} from 'features/core/shared/dto/dto.import.object.context.entry';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from 'features/core/shared/helper/widget-function-call-back-create';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {
    buildCpMatrixAgentQuery,
    buildCpWidgetForm,
    CP_MATRIX_AGENT_COLUMNS_FULL
} from '../../../../../service/domain/cp-widget-column';
import {
    buildCpAssignmentQuery,
    CP_ASSIGNMENT_LEADING_COLUMNS,
    CP_ASSIGNMENT_PERIOD_COLUMNS,
    CP_CONDITION_SET_FIELD_ID,
    CP_CONDITION_SET_JOIN_AS,
    CP_UNCONDITIONAL_LABEL,
    cpRowValue
} from '../../../../../service/domain/cp-provision-assignment-columns';

/**
 * navItem "Matrizen" of the ProvisionType object component — the two-level matrix family.
 *
 * ## Why a selectable master-detail is correct HERE
 * `cp.ProvisionTypeMatrix` (K-08) is a child of `cp.ProvisionType` and owns exactly ONE
 * same-module configuration child of its own: `cp.ProvisionTypeMatrixAgent` (K-09, mandatory FK
 * `provisionTypeMatrixDtoId`). One child means SIMPLE under the Complex Configuration Entity Rule,
 * and for simple entities the selectable-parent + FK-filtered-child pattern is the prescribed
 * form (`fe_core_page`, decision matrix "0 or 1 children"). Reference for the mechanics:
 * `features/feature-bm/bm/page/overview/config/config.page.ts` (creditor -> bank accounts,
 * PAT §M.2).
 *
 * `cp.ProvisionTypeMatrixAssignment` (K-10) carries a mandatory FK to the matrix too, but as the
 * RESULT of its assignment rule (`ProvisionTypeMatrixAssignment.getResult()`), not as its owner:
 * its owner is `cp.ProvisionType` (mandatory FK `provisionTypeDtoId`, declared first), exactly
 * like K-05/K-06/K-07 whose results are an eligibility, a provision base and a liability type.
 * Its PRIMARY maintenance surface is therefore the sibling navItem "Matrix-Zuordnung", where the
 * full DIM rule set of the provision type is edited and where 04-frontend.md §6 lists it.
 *
 * Once a matrix IS selected here, the assignments POINTING AT that matrix are shown underneath as
 * a second, matrix-scoped list — it answers "which assignments (and which condition sets) route to
 * this matrix?", which is only answerable from the matrix side. It uses its own alias
 * (`config.cp.ProvisionTypeMatrixAssignment.byMatrix.table`) so its table state stays independent
 * of the provision-type-scoped list on the sibling navItem (CONFIG-011: one alias = one widget
 * identity). Creating from here pre-fills BOTH mandatory FKs.
 *
 * ## State model (CONFIG-011)
 * Both level-2 widgets keep their alias constant across every selection so the user's column
 * widths and sorting survive a selection change. Without a matrix selected neither level-2 widget
 * is built at all — level 2 stays empty and the right-hand side only shows the hint to pick a
 * matrix (04-frontend.md §6).
 */
@Component({
    selector: 'cp-provision-type-matrix',
    templateUrl: './cp-provision-type-matrix.component.html',
    styleUrls: ['./cp-provision-type-matrix.component.scss'],
    standalone: false
})
export class CpProvisionTypeMatrixComponent implements OnInit, OnChanges {

    /** Stable widget aliases (CONFIG-011) — identical in the filtered and unfiltered state. */
    private static readonly cAliasMatrix = 'config.cp.ProvisionTypeMatrix.selectable';
    private static readonly cAliasMatrixAgent = 'config.cp.ProvisionTypeMatrixAgent.table';
    private static readonly cAliasMatrixAssignment = 'config.cp.ProvisionTypeMatrixAssignment.byMatrix.table';

    initialized: boolean;

    @Input() provisionTypeId: number;

    /** Selected matrix, `null` = nothing selected, level 2 stays empty. */
    selectedMatrixId: number | null = null;

    matrixWidget: WidgetData;
    matrixAgentWidget: WidgetData;
    matrixAssignmentWidget: WidgetData;

    matrixImportContext: DtoImportObjectContext;
    matrixAgentImportContext: DtoImportObjectContext;
    matrixAssignmentImportContext: DtoImportObjectContext;

    /** fieldId of the "Bedingungssatz" custom column of the matrix-scoped assignment list. */
    readonly conditionSetFieldId = CP_CONDITION_SET_FIELD_ID;
    readonly unconditionalLabel = CP_UNCONDITIONAL_LABEL;

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.refreshComponent();
    }

    refreshComponent(): void {

        if (!this.provisionTypeId) {
            this.matrixWidget = null;
            this.matrixAgentWidget = null;
            this.matrixAssignmentWidget = null;
            this.matrixImportContext = null;
            this.matrixAgentImportContext = null;
            this.matrixAssignmentImportContext = null;
            this.initialized = true;
            return;
        }

        this.refreshMatrixWidget();
        this.refreshMatrixAgentWidget();
        this.refreshMatrixAssignmentWidget();

        this.initialized = true;
    }

    /** Level 1 — the matrices of this provision type. */
    private refreshMatrixWidget(): void {

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Configuration
         * Reason: Selectable list of the provision matrices (cp.ProvisionTypeMatrix) of EXACTLY
         *     this provision type. The selection filters the matrix rows below; a new row gets
         *     its mandatory FK provisionType pre-filled.
         */
        this.matrixWidget = WidgetFactory.createWidgetSelectableEntityQl(
            CpProvisionTypeMatrixComponent.cAliasMatrix,
            'Provisionsmatrizen',
            'cp.ProvisionTypeMatrix',
            'Keine Provisionsmatrizen vorhanden',
            [FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual, this.provisionTypeId)],
            [new Sorting('e.name', true)],
            WidgetDataParam.create('attributeToDisplay', 'e.name'),
            WidgetDataParam.create('showSearch', true)
        );

        const createFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'provisionTypeDtoId', fieldValue: this.provisionTypeId}
        ];
        this.matrixWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(createFields);

        this.matrixImportContext = new DtoImportObjectContext(
            [new DtoImportObjectContextEntry('cp.ProvisionType', this.provisionTypeId)]);
    }

    /**
     * Level 2 — the matrix rows (agent / group factors) of the selected matrix.
     *
     * Only meaningful with a matrix selected, exactly like `refreshMatrixAssignmentWidget()`:
     * without one no level-2 widget is built and the template shows the hint to pick a matrix.
     *
     * Do NOT reintroduce a fallback that lists the rows of ALL matrices of this provision type: it
     * would need the transitive attribute `e.provisionTypeMatrix.provisionTypeId`, and a QL filter
     * attribute is limited to exactly two segments — `alias.fieldName` — by
     * `QlDpEntityJdbcConditionsResolver._resolveFilterAttribute` (throws QlRuntimeException
     * otherwise), so the whole panel fails to render. The empty state is also the specified
     * behaviour (04-frontend.md §6).
     */
    private refreshMatrixAgentWidget(): void {

        const matrixId = this.selectedMatrixId;

        if (!matrixId) {
            this.matrixAgentWidget = null;
            this.matrixAgentImportContext = null;
            return;
        }

        /**
         * EXPLICIT-JOIN table instead of `createWidgetTableEntityFkeyResolveQl`.
         *
         * The uncurated fkey-resolve table rendered EVERY projected attribute of the entity, so the
         * row opened on `Provisionsmatrix - ID`, `Gültig bis`, `Angelegt von`, `Id` … with `Id` four
         * times and `Name` three times, while factor, calculation mode, term band and entry age —
         * the whole business content — sat off-screen to the right.
         *
         * QUERY AND COLUMN SET NOW COME FROM THE MODULE HELPER, not from a literal here. The same
         * table has a second host — the ProvisionTypeMatrix OBC, which the inheritance-chain chip of
         * the Beratersicht opens — and two hand-written copies of the same joins and headers would
         * drift silently: a QL field path that no join produces renders an EMPTY column, without a
         * compile error. `buildCpMatrixAgentQuery` and `CP_MATRIX_AGENT_COLUMNS_FULL`
         * (`service/domain/cp-widget-column.ts`) reproduce what stood here field for field and
         * header for header, and they carry the full reasoning for the explicit joins — why
         * `resolve_fkey_texts` cannot reach the Beratername two hops out, and why the group side is
         * joined explicitly rather than through the positional `fkeyt_e_<N>` alias.
         *
         * The query NAME stays `config.cp.ProvisionTypeMatrixAgent` and the widget alias stays
         * {@link cAliasMatrixAgent}: the user's column widths and sorting hang off that alias.
         *
         * Same shape as the module's two other curated tables:
         * `cp-provision-type-base.component.ts:399-484` (K-17, calculated agents) and
         * `cp-provision-agent-contract-agents.component.ts:337-414` (Provisionssätze).
         */

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Configuration
         * Reason: Matrix rows (cp.ProvisionTypeMatrixAgent) — the provision amount per agent
         *     or agent group, filtered on the SELECTED matrix. Curated to the business columns in
         *     reading order "wer → wie viel → wie gerechnet → wofür → wann": agent and agent group
         *     in plain text, factor and calculation mode, the term band and the entry-age band that
         *     narrow the row, the liability-type override, and the validity period plus status —
         *     the same trailing block the sibling assignment tables carry. The matrix column is
         *     omitted: the list IS filtered on it, so it is constant by construction. A new row gets
         *     its mandatory FK provisionTypeMatrix pre-filled.
         */
        this.matrixAgentWidget = WidgetFactory.createWidgetTableQl(
            CpProvisionTypeMatrixComponent.cAliasMatrixAgent,
            'Zeilen der gewählten Matrix',
            buildCpMatrixAgentQuery('config.cp.ProvisionTypeMatrixAgent', matrixId),
            WidgetDataParam.create('widgetForm', buildCpWidgetForm(CP_MATRIX_AGENT_COLUMNS_FULL))
        );

        const createFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'provisionTypeMatrixDtoId', fieldValue: matrixId}
        ];
        this.matrixAgentWidget.functionCallbacks =
            WidgetFunctionCallBackCreate.widgetToolbarCreate(createFields);

        this.matrixAgentImportContext = new DtoImportObjectContext(
            [new DtoImportObjectContextEntry('cp.ProvisionTypeMatrix', matrixId)]);
    }

    /**
     * Level 2b — the assignment rules that RESULT IN the selected matrix.
     *
     * Only meaningful with a matrix selected: without one the sibling navItem
     * "Matrix-Zuordnung" already shows the provision type's complete rule set.
     */
    private refreshMatrixAssignmentWidget(): void {

        const matrixId = this.selectedMatrixId;

        if (!matrixId) {
            this.matrixAssignmentWidget = null;
            this.matrixAssignmentImportContext = null;
            return;
        }

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Configuration
         * Reason: Assignment rules (cp.ProvisionTypeMatrixAssignment) that point at the SELECTED
         *     matrix. Answers the question — askable only from the matrix side — of which
         *     assignments (and through which condition sets) route to this matrix in the first
         *     place. Same fixed column block as the Matrix-Zuordnung nav point: name, alias,
         *     condition set (empty = "Alle Verträge (unbedingt)"), priority, commission component,
         *     period, status and the liability override. The result column (the matrix) is
         *     omitted — it is constant here by construction, because the list IS filtered on it.
         */
        const query: QlQueryDto = {
            ...buildCpAssignmentQuery(
                'config.cp.ProvisionTypeMatrixAssignment.byMatrix',
                'cp.ProvisionTypeMatrixAssignment',
                [
                    {relation: 'provisionTypeComponent', as: 'ptc'},
                    {relation: 'liabilityTypeOverwrite', as: 'lto'}
                ]),
            fixedFilters: [
                FilterCriteria.create('e.provisionTypeMatrixId', FilterCriteria.cOperatorEqual, matrixId)
            ]
        };

        this.matrixAssignmentWidget = WidgetFactory.createWidgetTableQl(
            CpProvisionTypeMatrixComponent.cAliasMatrixAssignment,
            'Zuordnungen auf diese Matrix',
            query,
            WidgetDataParam.create('widgetForm', buildCpWidgetForm([
                ...CP_ASSIGNMENT_LEADING_COLUMNS,
                {field: 'ptc.name', label: 'Provisionskomponente'},
                ...CP_ASSIGNMENT_PERIOD_COLUMNS,
                {field: 'lto.name', label: 'Haftungsart (Überschreibung)'}
            ]))
        );

        // Both FKs of the assignment are mandatory, so both are pre-filled and read-only here
        // (CONFIG-012): the owner (provision type) and the result (this matrix).
        const createFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'provisionTypeDtoId', fieldValue: this.provisionTypeId},
            {fieldName: 'provisionTypeMatrixDtoId', fieldValue: matrixId}
        ];
        this.matrixAssignmentWidget.functionCallbacks =
            WidgetFunctionCallBackCreate.widgetToolbarCreate(createFields);

        this.matrixAssignmentImportContext = new DtoImportObjectContext([
            new DtoImportObjectContextEntry('cp.ProvisionType', this.provisionTypeId),
            new DtoImportObjectContextEntry('cp.ProvisionTypeMatrix', matrixId)
        ]);
    }

    /** Name of the condition set of one assignment row; `null` = unconditional. */
    conditionSetName(object: any): string | null {
        const name = cpRowValue(object, CP_CONDITION_SET_JOIN_AS + '.name');
        return typeof name === 'string' && name.trim().length ? name : null;
    }

    onMatrixSelect(event: ObjectIdentifier | ObjectIdentifierData): void {
        const matrixId = this.resolveObjectId(event);
        if (!matrixId) {
            return;
        }
        this.selectedMatrixId = matrixId;
        this.refreshMatrixAgentWidget();
        this.refreshMatrixAssignmentWidget();
    }

    onMatrixDeselect(): void {
        this.selectedMatrixId = null;
        this.refreshMatrixAgentWidget();
        this.refreshMatrixAssignmentWidget();
    }

    /** Robust id extraction — selectable and table widgets fill `objectId`, QL rows `e.id`. */
    private resolveObjectId(event: ObjectIdentifier | ObjectIdentifierData): number | null {
        if (!event) {
            return null;
        }
        if (event.objectId) {
            return +event.objectId;
        }
        const data = (event as ObjectIdentifierData).data;
        return data && data['e.id'] ? +data['e.id'] : null;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionTypeId']) {
            this.selectedMatrixId = null;
            this.refreshComponent();
        }
    }

}
